<template>
  <Card variant="outline" size="medium">
    <View gap="md" direction="column">
      <View>
        <Text variant="h4">{{ application.renter.user.name }}</Text>
        <Text v-if="application.type === ApplicantType.CO_SIGNER" color="tertiary" variant="h4">&nbsp;∙ Co-signer</Text>
      </View>

      <RenterProfileApplicationArtifact
        v-if="application.type !== ApplicantType.CO_SIGNER"
        title="Application"
        description="Get a complete view of the renter's personal info, job history, and income."
      >
        <Button @click="showSelfReportModal" class="artifact-action" size="small" variant="outline">View</Button>
      </RenterProfileApplicationArtifact>

      <RenterProfileApplicationArtifact
        :validityText="reportsExpiration.validityText"
        :expired="reportsExpiration.expired"
        title="Credit report"
        description="View the renter's credit score and history through TransUnion for clear financial insights."
      >
        <Button
          :to="reportRoute(ScreeningProductType.CREDIT)"
          v-if="!reportsExpiration.expired"
          class="artifact-action"
          size="small"
          variant="outline"
        >
          View
        </Button>
      </RenterProfileApplicationArtifact>

      <RenterProfileApplicationArtifact
        v-if="application.type !== ApplicantType.CO_SIGNER"
        :validityText="reportsExpiration.validityText"
        :expired="reportsExpiration.expired"
        title="Background report"
        :description="criminalArtifactDescription"
      >
        <Button
          v-if="!reportsExpiration.expired && criminalReportIsViewable"
          :to="reportRoute(ScreeningProductType.CRIMINAL)"
          class="artifact-action"
          size="small"
          variant="outline"
        >
          View
        </Button>
        <Button
          v-else-if="!reportsExpiration.expired"
          data-manage-action
          @click="showApproveCriminalScreeningModal()"
          class="artifact-action"
          size="small"
          variant="outline"
        >
          Approve to view
        </Button>
      </RenterProfileApplicationArtifact>

      <RenterProfileApplicationArtifact
        v-if="application.type !== ApplicantType.CO_SIGNER"
        :validityText="reportsExpiration.validityText"
        :expired="reportsExpiration.expired"
        title="Eviction report"
        :description="evictionArtifactDescription"
      >
        <Button
          :to="reportRoute(ScreeningProductType.EVICTION)"
          v-if="!reportsExpiration.expired"
          class="artifact-action"
          size="small"
          variant="outline"
        >
          View
        </Button>
      </RenterProfileApplicationArtifact>
    </View>

    <RenterProfileSelfReportModal :application="application" ref="applicationSelfReportModalRef" />
    <ApproveCriminalScreeningModal
      :application-id="application.id"
      ref="approveCriminalScreeningModalRef"
      @approved="openCriminalReport()"
    />
  </Card>
</template>

<script setup lang="ts">
import { computed, ref } from 'vue';

import {
  ApplicantType,
  ApplicationWithRenterDto,
  BackgroundRecordsProvider,
  ScreeningProductType,
  getApplicationReportExpirationData,
  getReportFormat,
  isBifurcatedScreeningRequired,
} from '@tallo/applications';
import { Button, Card, Text, View } from '@tallo/design-system';
import { ApplicationBundlePropertyLocationDto } from '@tallo/investor/applications';
import { renterTransUnionReportRoute, useNavigation } from '@tallo/investor/navigation';

import ApproveCriminalScreeningModal from './approve-criminal-screening-modal.vue';
import RenterProfileApplicationArtifact from './renter-profile-application-artifact.vue';
import RenterProfileSelfReportModal from './renter-profile-self-report-modal.vue';

const applicationSelfReportModalRef = ref<InstanceType<typeof RenterProfileSelfReportModal>>();
const approveCriminalScreeningModalRef = ref<InstanceType<typeof ApproveCriminalScreeningModal>>();

const props = defineProps<{
  application: ApplicationWithRenterDto;
  propertyLocation?: ApplicationBundlePropertyLocationDto;
}>();

const { openRenterTransUnionReportRoute } = useNavigation();

const reportsExpiration = computed(() => getApplicationReportExpirationData(props.application.expiresAt));

const requiresApproval = computed(() => isBifurcatedScreeningRequired(props.propertyLocation));
const approved = computed(() => !!props.application.criminalScreeningApprovedAt);

const criminalReportIsViewable = computed(() => !requiresApproval.value || approved.value);

// Criminal and eviction come from whichever vendor screened this application; credit is always TransUnion.
const recordsProviderName = computed(() =>
  props.application.backgroundRecordsProvider === BackgroundRecordsProvider.RENT_PREP ? 'RentPrep' : 'TransUnion',
);

const criminalArtifactDescription = computed(() =>
  requiresApproval.value && !approved.value
    ? 'This property is in a jurisdiction that requires you to confirm pre-qualification on credit, income and rental history before the background report can be shown.'
    : `Review a detailed background check provided by the renter, verified through ${recordsProviderName.value}.`,
);

const evictionArtifactDescription = computed(
  () => `Review a detailed eviction history provided by the renter, verified through ${recordsProviderName.value}.`,
);

function reportRoute(type: ScreeningProductType) {
  return renterTransUnionReportRoute(props.application.id, type, getReportFormat(props.application, type));
}

function showSelfReportModal(): void {
  applicationSelfReportModalRef.value?.modalRef?.show();
}

function showApproveCriminalScreeningModal(): void {
  approveCriminalScreeningModalRef.value?.modalRef?.show();
}

function openCriminalReport(): void {
  void openRenterTransUnionReportRoute(
    props.application.id,
    ScreeningProductType.CRIMINAL,
    getReportFormat(props.application, ScreeningProductType.CRIMINAL),
  );
}
</script>

<style scoped lang="scss">
@import 'styles/mixins/layout.mixins';

.artifact-action {
  width: 100%;
  max-width: 24rem;
  align-self: center;
}

@include screen-min-sm {
  .artifact-action {
    width: fit-content;
  }
}
</style>
